// Origin enum for mfg_units (Fulfillment Slice 1).
//
// Mirrors the `origin` column added to `mfg_units` by
// docs/sql/fulfillment_intake_v1.sql. Kept in src/lib/fulfillment/
// rather than src/lib/manufacturing/ because the *non-default* values
// (cash_purchase / repo_intake) are owned by the Fulfillment bucket --
// the manufacturing pipeline only ever produces `manufactured` units
// (planning doc §5.2).
//
// Pure helper: no imports from React, no imports from Supabase. Safe
// to call from both the client (entry-form validation) and the server
// (handler validation) without dragging in a runtime.

export type MfgUnitOrigin = "manufactured" | "cash_purchase" | "repo_intake";

export const VALID_MFG_UNIT_ORIGINS: readonly MfgUnitOrigin[] = [
  "manufactured",
  "cash_purchase",
  "repo_intake",
];

/** The two origin values that route through the Fulfillment bucket's New-inventory entry surface. */
export const FULFILLMENT_ENTRY_ORIGINS: readonly MfgUnitOrigin[] = [
  "cash_purchase",
  "repo_intake",
];

/** Type-guard. Returns true iff `v` is a known origin literal. */
export function isMfgUnitOrigin(v: unknown): v is MfgUnitOrigin {
  return (
    typeof v === "string" &&
    (VALID_MFG_UNIT_ORIGINS as readonly string[]).includes(v)
  );
}

/** True iff the origin requires a sentinel serial prefix (CASH-#### / REPO-####) rather than a plant-prefix serial. */
export function originRequiresSentinelSerial(origin: MfgUnitOrigin): boolean {
  return origin === "cash_purchase" || origin === "repo_intake";
}

/** Plant-prefix serials for manufactured and for unknown/null origin (treat as manufactured). */
export function originUsesPlantPrefixSerial(origin: string | null | undefined): boolean {
  return origin !== "cash_purchase" && origin !== "repo_intake";
}

/**
 * Maps a non-default origin to its serial prefix.
 * Returns null for `manufactured` (uses the plant-prefix serial scheme).
 */
export function sentinelPrefixForOrigin(
  origin: MfgUnitOrigin
): "CASH" | "REPO" | null {
  if (origin === "cash_purchase") return "CASH";
  if (origin === "repo_intake") return "REPO";
  return null;
}
